import { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router";
import { useSnackbar } from "notistack";
import { Box, ButtonBase, Link, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";

const MAX_ROWS = 5;

/**
 * The dashboard's Sparte card: up to five Sparten among the Verträge the
 * user may see, the ones with the most Verträge first - the full count per
 * Sparte comes from GET /data/vertrags/sparte-counts, which also does the
 * ordering and the cap; "Alle" links to the unfiltered contract list for
 * the rest. Clicking a row opens the contract list filtered to
 * that Sparte, the same mechanism KP-07 built for the Sparte filter - keyed
 * by the Sparte's catalog id (sparte.id), not its label, so it stays in
 * step with Vertrag::scopeSparte() matching on objektspartes.sparte_id.
 *
 * The card chrome (border, radius, grey header bar) matches DataTable's own
 * card styling, so this reads as the same kind of card as the contract
 * table below it rather than a plain list.
 */
export default function SparteSummaryCard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [sparteCounts, setSparteCounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    backendGet("/data/vertrags/sparte-counts", {
      label: "Sparten",
      enqueueSnackbar,
      options: { params: { limit: MAX_ROWS } },
    })
      .then((response) => {
        if (Array.isArray(response?.data)) {
          setSparteCounts(response.data);
        }
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowSx = (index) => ({
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    px: { xs: 2, md: 4 },
    py: 1.75,
    borderTop:
      index === 0 ? "none" : `1px solid ${theme.palette.grey[200]}`,
  });

  return (
    <Box
      sx={{
        mb: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: theme.spacing(1),
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "card.headerBackground",
          px: { xs: 2, md: 4 },
          py: { xs: 1, md: 2.5 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
        >
          Meine Verträge nach Sparte
        </Typography>
        <Link component={RouterLink} to="/vertraege" underline="hover">
          Alle
        </Link>
      </Stack>

      <Stack>
        {isLoading
          ? Array.from({ length: MAX_ROWS }).map((_, index) => (
              <Box key={index} data-testid="sparte-row-skeleton" sx={rowSx(index)}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="20%" />
              </Box>
            ))
          : sparteCounts.map((entry, index) => (
              <ButtonBase
                key={entry.id}
                data-testid={`sparte-row-${entry.sparte}`}
                onClick={() => navigate(`/vertraege?sparte=${entry.id}`)}
                sx={{
                  ...rowSx(index),
                  "&:hover": {
                    backgroundColor:
                      componentTokens.dataTable.row.hoverBackground,
                  },
                }}
              >
                <Typography variant="body2">{entry.sparte}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.count} {entry.count === 1 ? "Vertrag" : "Verträge"}
                </Typography>
              </ButtonBase>
            ))}
      </Stack>
    </Box>
  );
}
